import { useCallback } from 'react';

import {
	ufoEventE2eProvisioningLatency,
	ufoEventProductSetupLatency,
} from '@/components/signup/utils/ufo-events';
import { SignupUfoCustomData } from '@/components/signup/utils/ufo.types';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags as gates } from '@/utils/feature-flags/feature-flag-manifest';

type FinalProvisioningScreen = NonNullable<SignupUfoCustomData['finalProvisioningScreen']>;

export const useProvisioningLatencyUfo = () => {
	const isProvisioningLatencyUfoEnabled = checkFeatureFlagGate(
		gates.wacUfoEventProvisioningLatency,
	);

	const startE2eProvisioning = useCallback(
		(options: Parameters<typeof ufoEventE2eProvisioningLatency.start>[0]) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventE2eProvisioningLatency.start(options);
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const startProductSetup = useCallback(
		(options: Parameters<typeof ufoEventProductSetupLatency.start>[0]) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventProductSetupLatency.start(options);
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const completeProductSetup = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventProductSetupLatency.success({ customData: { finalProvisioningScreen } });
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const completeE2eProvisioning = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			if (isProvisioningLatencyUfoEnabled) {
				void ufoEventE2eProvisioningLatency.success({ customData: { finalProvisioningScreen } });
			}
		},
		[isProvisioningLatencyUfoEnabled],
	);

	const completeAll = useCallback(
		(finalProvisioningScreen: FinalProvisioningScreen) => {
			completeProductSetup(finalProvisioningScreen);
			completeE2eProvisioning(finalProvisioningScreen);
		},
		[completeProductSetup, completeE2eProvisioning],
	);

	return {
		startE2eProvisioning,
		startProductSetup,
		completeProductSetup,
		completeE2eProvisioning,
		completeAll,
		isProvisioningLatencyUfoEnabled,
	};
};
